// src/services/contractServices.ts
import axiosInstance from '../utils/axiosConfig';
import { Venue } from '../types/mongodb';
import { BandRecommendation } from '../types/BandRecommendations';
import { FormData } from '../components/contracts/types';

// Add these new formatting functions for human-readable output
function formatReadableDate(date: Date): string {
  return date.toLocaleDateString('en-IE', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}

function formatReadableTime(date: Date): string {
  return date.toLocaleTimeString('en-IE', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
}

// Helper functions for other date/time formatting needs
const formatToLocaleDateString = (value: Date | string | null): string | null => {
  if (!value) return null;
  if (value instanceof Date) return value.toLocaleDateString();
  try {
    return new Date(value).toLocaleDateString();
  } catch {
    return null;
  }
};

const formatToLocaleTimeString = (value: Date | string | null): string | null => {
  if (!value) return null;
  if (value instanceof Date) return value.toLocaleTimeString();
  try {
    return new Date(value).toLocaleTimeString();
  } catch {
    return null;
  }
};

const formatToISOString = (value: Date | string | null): string | null => {
  if (!value) return null;
  if (value instanceof Date) return value.toISOString();
  try {
    return new Date(value).toISOString();
  } catch {
    return null;
  }
};

interface ServiceResponse<T> {
  data: T[];
  message?: string;
  status: number;
}

interface GenerateContractParams {
  data: FormData;
  mode?: 'create' | 'edit';
  originalContractId?: string;
}

interface VenueResponse {
  _id: string;
  name: string;
  location: string;
  rentalCost: number;
  capacity: number;
  createdAt: Date;
  updatedAt: Date;
}

interface EmailContractParams {
  contractPath: string;
  contractData: FormData;
  recipient: string;
  recipientName?: string;
  sendCopy?: boolean;
  testMode?: boolean;
}

export const contractServices = {
  getVenues: async (): Promise<Venue[]> => {
    try {
      const response = await axiosInstance.get<VenueResponse[]>('/api/venues');
      // Response is the array directly, not nested in data property
      return response.data.map(venue => ({
        ...venue,
        rentalCost: venue.rentalCost || 0,
        createdAt: venue.createdAt || new Date(),
        updatedAt: venue.updatedAt || new Date()
      }));
    } catch (error) {
      console.error('Error fetching venues:', error);
      return [];
    }
  },

  getArtists: async (searchTerm?: string): Promise<BandRecommendation[]> => {
    try {
      const response = await axiosInstance.get<BandRecommendation[]>(
        `/api/bandrecommendations/search`,
        { 
          params: { 
            q: searchTerm
          } 
        }
      );
      return response.data || [];
    } catch (error) {
      console.error('Error searching artists:', error);
      return [];
    }
  },

  generateContract: async ({ data, mode, originalContractId }: GenerateContractParams): Promise<string> => {
    console.log('Contract Generation Params:', {
      mode,
      originalContractId,
      contractNumber: data.contractNumber
    });

    const requestData = {
      data: {
        ...data,
        // Format dates to be human-readable before sending to template
        'Event Date': data['Event Date'] instanceof Date 
          ? formatReadableDate(data['Event Date']) 
          : typeof data['Event Date'] === 'string' 
            ? formatReadableDate(new Date(data['Event Date'])) 
            : null,
        'Soundcheck Time': data['Soundcheck Time'] instanceof Date 
          ? formatReadableTime(data['Soundcheck Time']) 
          : typeof data['Soundcheck Time'] === 'string' 
            ? formatReadableTime(new Date(data['Soundcheck Time'])) 
            : null,
        'Stage Time': data['Stage Time'] instanceof Date 
          ? formatReadableTime(data['Stage Time']) 
          : typeof data['Stage Time'] === 'string' 
            ? formatReadableTime(new Date(data['Stage Time'])) 
            : null,
        'Doors': data['Doors'] instanceof Date 
          ? formatReadableTime(data['Doors']) 
          : typeof data['Doors'] === 'string' 
            ? formatReadableTime(new Date(data['Doors'])) 
            : null,
      },
      mode: mode || 'create',
      originalContractId
    };

    console.log('Request to backend:', JSON.stringify(requestData, null, 2));

    try {
      const response = await axiosInstance.post('/api/contracts/generate', requestData);
      console.log('Generation response:', response.data);
      return response.data.url;
    } catch (error) {
      console.error('Contract generation error:', error);
      throw error;
    }
  },

  updateContract: async (contractId: string, formData: FormData): Promise<void> => {
    try {
      // Transform form data back to API format
      const apiData = {
        artistName: formData.Item,
        email: formData.Email,
        phone: formData.Phone,
        eventDate: formatToISOString(formData['Event Date']),
        venue: formData.Venue,
        address: formData.Address,
        soundcheckTime: formatToISOString(formData['Soundcheck Time']),
        soundcheckDuration: formData['Soundcheck Duration'],
        doorsTime: formatToISOString(formData.Doors),
        stageTime: formatToISOString(formData['Stage Time']),
        performanceType: formData['Performance Type'],
        minDuration: formData['Min Duration'],
        fees: formData.Fees,
        paymentType: formData['Payment Type'],
        intlFlights: formData['Int\'l Flights'],
        groundTransport: formData['Ground Transport'],
        accommodationType: formData['Accommodation Type'],
        accommodationQty: formData['Accommodation Qty'],
        pdAmount: formData['PD Amount'],
        pdQuantity: formData['PD Quantity'],
        pdDays: formData['PD Days'],
        activitiesMaster: formData['Activities Master'],
        imcCode: formData['IMC Code'],
        contractPath: formData.contractPath,
      };

      await axiosInstance.put(`/api/contracts/${contractId}`, apiData);
    } catch (error) {
      console.error('Error updating contract:', error);
      throw error;
    }
  },

  downloadContract: async (url: string): Promise<Blob> => {
    try {
      // Extract filename from url
      const filename = url.split('/').pop();
      // Use our proxy endpoint instead of direct GCS url
      const response = await axiosInstance.get(`/api/contracts/download/${filename}`, {
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      console.error('Contract download error:', error);
      throw error;
    }
  },

  emailContract: async (params: EmailContractParams): Promise<{success: boolean; message: string; messageId?: string}> => {
    try {
      // Format dates properly for the email service
      const formattedContractData = {
        ...params.contractData,
        // Use ISO strings for the backend API, but human-readable dates will be in the document
        'Event Date': formatToISOString(params.contractData['Event Date']),
        'Soundcheck Time': formatToISOString(params.contractData['Soundcheck Time']),
        'Doors': formatToISOString(params.contractData['Doors']),
        'Stage Time': formatToISOString(params.contractData['Stage Time']),
      };
      
      const response = await axiosInstance.post('/api/email/contract', {
        contractDocPath: params.contractPath,
        contractData: formattedContractData,
        recipient: params.recipient,
        recipientName: params.recipientName || formattedContractData.Contact,
        sendCopy: params.sendCopy !== false, // Default to true
        testMode: params.testMode || false
      });
      
      return response.data;
    } catch (error) {
      console.error('Error sending contract email:', error);
      throw new Error(`Failed to send contract email: ${error instanceof Error ? error.message : String(error)}`);
    }
  }
};